import {
	BigNum,
	ZERO,
	calculateMaxRemainingDeposit,
	QUOTE_PRECISION_EXP,
	SpotMarketConfig,
} from '@drift-labs/sdk';
import { UIMarket, MarketId, truncateInputToPrecision } from '@drift/common';
import React, { useEffect, useMemo, useState, useRef } from 'react';
import { useDepositCollateral } from 'src/hooks/driftActions/spot-management/useDepositCollateral';
import useDriftClient from 'src/hooks/useDriftClient';
import useDriftClientIsReady from 'src/hooks/useDriftClientIsReady';
import useTransferableWalletCollateralBalance from 'src/hooks/useTransferableWalletCollateralBalance';
import { getCloseBorrowAmountWithBuffer } from 'src/utils/borrowLend';
import { getCleanedMarketSymbol } from 'src/utils/market';
import { toNotional } from 'src/utils/formatting';
import useCollateralModalState from 'src/components/Modals/CollateralModal/hooks/useCollateralModalState';
import { CollateralModalProps } from 'src/components/Modals/CollateralModal/types';
import { useQueryClient } from '@tanstack/react-query';
import { useIsSignedMsgUserOrdersAccountInitialized } from 'src/hooks/signedMsgOrders/useIsSignedMsgUserOrdersAccountInitialized';
import FixedAssetInput from 'src/components/Inputs/FixedAssetInput';
import useAccountTargetSpotBalance from 'src/hooks/useAccountTargetSpotBalance';
import Button from 'src/components/primitives/Button/Button';
import { useSpotMarketsSortedByMarketIndex } from 'src/hooks/spotMarkets/useSpotMarketsSortedByMarketIndex';
import { InputQuickActions } from 'src/components/Inputs/InputQuickActions';
import useGetOraclePriceForMarket from 'src/hooks/useGetOraclePriceForMarket';
import useDriftAccountStore from 'src/stores/useDriftAccountsStore';
import { useSpotMarketsLookup } from 'src/hooks/spotMarkets/useSpotMarketsLookup';
import { CollateralInputV2 } from 'src/components/Inputs/CollateralInput';
import useSwapFormEngine from 'src/hooks/swap/useSwapFormEngine';
import { DEFAULT_SWAP_SLIPPAGE_TOLERANCE_PCT } from 'src/constants/swap';
import { useTokenPairData } from 'src/hooks/swap/useTokenPairData';
import { SwapPrice } from 'src/components/Swap/SwapForm';
import useCurrentSettings from 'src/hooks/useCurrentSettings';
import DefinitionTooltip from 'src/components/Tooltip/DefinitionTooltip/DefinitionTooltip';
import CollapsibleSlippageToleranceInput from 'src/components/TradeForm/SlippageToleranceInput/CollapsibleSlippageToleranceInput';
import Switch from 'src/components/primitives/Switch/Switch';
import useIsMobileScreenSize from 'src/hooks/useIsMobileScreenSize';
import useModal from 'src/components/Modals/hooks/useModal';
import BaseModal from 'src/components/primitives/BaseModal/BaseModal';
import InlineAlert from 'src/components/primitives/InlineAlert/InlineAlert';
import ToggleTabs from 'src/components/primitives/ToggleTabs/ToggleTabs';
import Divider from 'src/components/primitives/Divider/Divider';
import {
	MODAL_PADDING,
	MODAL_BG_CLASSNAME,
} from 'src/components/primitives/BaseModal/consts';
import { cj } from 'src/utils/classNames';

const RepayTabLabel = {
	deposit: 'Deposit',
	swaps: 'Swaps',
} as const;

type RepayTab = keyof typeof RepayTabLabel;

const RepayTabOptions: { value: RepayTab; label: string }[] = [
	{ value: 'deposit', label: RepayTabLabel.deposit },
	{ value: 'swaps', label: RepayTabLabel.swaps },
];

const RepayDepositTab = ({
	spotMarketIndex,
	targetAccountKey,
	onClose,
}: {
	spotMarketIndex: number;
	targetAccountKey?: string;
	onClose: () => void;
}) => {
	const spotMarketsSortedByMarketIndex = useSpotMarketsSortedByMarketIndex();
	const selectedCollatBank = spotMarketsSortedByMarketIndex[spotMarketIndex];
	const selectedCollatUISpotMarketConfig =
		UIMarket.createSpotMarket(spotMarketIndex);
	const { depositCollateralForExistingAccount } = useDepositCollateral();
	const queryClient = useQueryClient();
	const { data: isSignedMsgUserOrdersAccountInitialized } =
		useIsSignedMsgUserOrdersAccountInitialized();
	const [isSubmitting, setIsSubmitting] = useState(false);

	const [currentUserKey, accounts] = useDriftAccountStore((s) => [
		s.currentUserKey,
		s.accounts,
	]);
	const userKey = targetAccountKey ? targetAccountKey : currentUserKey;

	const driftClientIsReady = useDriftClientIsReady();
	const driftClient = useDriftClient();
	const targetAccountBankBalance = useAccountTargetSpotBalance(
		selectedCollatBank,
		userKey
	);
	const [depositableBalanceBigNum, loadingWalletDepositableBalance] =
		useTransferableWalletCollateralBalance(selectedCollatBank, undefined);

	const [collatTransferAmount, setCollatTransferAmount] = useState('');
	const [isMaxRepay, setIsMaxRepay] = useState(false);
	const hasUserUpdatedInput = useRef(false);

	const getOraclePriceForMarket = useGetOraclePriceForMarket();

	// Get target account data for calculating net USD value
	const targetAccountData = useDriftAccountStore(
		(s) => s.accounts[userKey ?? '']
	);

	const bufferedCloseBorrowAmount = driftClient
		? getCloseBorrowAmountWithBuffer(
				driftClient,
				spotMarketIndex,
				targetAccountBankBalance?.netBaseBalance ?? BigNum.zero()
			)
		: BigNum.zero(selectedCollatBank.precisionExp);

	const spotMarketAccount =
		(driftClientIsReady &&
			driftClient?.getSpotMarketAccount(spotMarketIndex)) ||
		undefined;

	const remainingDepositsAllowed = useMemo(
		() =>
			spotMarketAccount
				? BigNum.max(
						BigNum.from(
							calculateMaxRemainingDeposit(spotMarketAccount),
							selectedCollatBank.precisionExp
						),
						bufferedCloseBorrowAmount
					)
				: BigNum.zero(selectedCollatBank.precisionExp),
		[selectedCollatBank.precisionExp, spotMarketAccount]
	);

	const showDepositCap =
		spotMarketAccount?.maxTokenDeposits.gt(ZERO) &&
		depositableBalanceBigNum.gt(remainingDepositsAllowed);

	const effectiveDepositableBalanceBigNum = showDepositCap
		? remainingDepositsAllowed
		: depositableBalanceBigNum;

	const collatTransferAmountBigNum = useMemo(
		() =>
			BigNum.fromPrint(
				collatTransferAmount || '0',
				selectedCollatBank.precisionExp
			),
		[collatTransferAmount, selectedCollatBank.precisionExp]
	);
	const exceededMax = useMemo(
		() =>
			collatTransferAmountBigNum.gt(effectiveDepositableBalanceBigNum) &&
			!loadingWalletDepositableBalance,
		[
			collatTransferAmountBigNum,
			effectiveDepositableBalanceBigNum,
			loadingWalletDepositableBalance,
		]
	);

	// Calculate the projected net account value after deposit
	const assetCurrentPrice = getOraclePriceForMarket(
		MarketId.createSpotMarket(spotMarketIndex)
	);

	const currentNetQuoteValue = BigNum.from(
		targetAccountData?.client?.getNetUsdValue() ?? 0,
		QUOTE_PRECISION_EXP
	);

	const quoteAmountToAdd = useMemo(() => {
		if (!assetCurrentPrice) return BigNum.zero(QUOTE_PRECISION_EXP);

		return selectedCollatBank?.symbol === 'USDC'
			? collatTransferAmountBigNum.shiftTo(QUOTE_PRECISION_EXP)
			: collatTransferAmountBigNum
					.shiftTo(QUOTE_PRECISION_EXP)
					.mul(assetCurrentPrice)
					.shiftTo(QUOTE_PRECISION_EXP);
	}, [
		collatTransferAmountBigNum,
		assetCurrentPrice,
		selectedCollatBank?.symbol,
	]);

	const projectedNetAccountValue = currentNetQuoteValue.add(quoteAmountToAdd);

	const isButtonDisabled =
		collatTransferAmountBigNum.eqZero() ||
		collatTransferAmountBigNum.gt(effectiveDepositableBalanceBigNum);

	useEffect(() => {
		if (
			!hasUserUpdatedInput.current &&
			!targetAccountBankBalance.netBaseBalance.eqZero()
		) {
			handleMaxRepay();
		}
	}, [bufferedCloseBorrowAmount.print()]);

	useEffect(() => {
		if (exceededMax) {
			setCollatTransferAmount(effectiveDepositableBalanceBigNum.print());
		}
	}, [exceededMax, effectiveDepositableBalanceBigNum]);

	const handleDeposit = async () => {
		setIsSubmitting(true);

		const collateralAmount = BigNum.fromPrint(
			collatTransferAmount,
			selectedCollatBank.precisionExp
		);

		const success = await depositCollateralForExistingAccount(
			collateralAmount,
			selectedCollatBank,
			userKey,
			isMaxRepay,
			!isSignedMsgUserOrdersAccountInitialized
		);

		if (success) {
			if (queryClient) {
				queryClient.invalidateQueries({
					queryKey: [
						'isSignedMsgUserOrdersAccountInitialized',
						accounts[userKey ?? '']?.authority?.toBase58(),
					],
				});
			}

			onClose();
		}

		setIsSubmitting(false);
	};

	const handleValueChange = (newValue?: string) => {
		if (!newValue) return;
		setIsMaxRepay(false);
		setCollatTransferAmount(newValue);
		hasUserUpdatedInput.current = true;
	};

	const handleMaxRepay = () => {
		setCollatTransferAmount(bufferedCloseBorrowAmount.print());
		setIsMaxRepay(true);
		hasUserUpdatedInput.current = false;
	};

	const handleFiftyPercentWallet = () => {
		const fiftyPercent = effectiveDepositableBalanceBigNum.div(BigNum.from(2));
		setCollatTransferAmount(fiftyPercent.print());
		setIsMaxRepay(false);
		hasUserUpdatedInput.current = true;
	};

	const handleMaxWallet = () => {
		setCollatTransferAmount(effectiveDepositableBalanceBigNum.print());
		setIsMaxRepay(false);
		hasUserUpdatedInput.current = true;
	};

	const handleFiftyPercentRepay = () => {
		const fiftyPercent = bufferedCloseBorrowAmount.div(BigNum.from(2));
		setCollatTransferAmount(fiftyPercent.print());
		setIsMaxRepay(false);
		hasUserUpdatedInput.current = true;
	};

	const baseAssetDisplaySymbol =
		selectedCollatUISpotMarketConfig.uiSymbols.baseAssetDisplaySymbol;

	return (
		<>
			<BaseModal.Body className="pt-0">
				<div className="flex flex-col gap-4">
					<div className="flex flex-col gap-1">
						<span>Assets Borrowed and Amount</span>
						<FixedAssetInput
							selectedMarket={selectedCollatBank}
							onChangeValue={handleValueChange}
							value={collatTransferAmount}
						/>
					</div>

					<div className="flex flex-col gap-2">
						<div className="flex items-center justify-between">
							<span>Wallet Balance</span>
							<div className="flex items-center gap-1">
								<InputQuickActions.FiftyPercentButton
									onClick={handleFiftyPercentWallet}
								/>
								<InputQuickActions.MaxButton
									onClick={handleMaxWallet}
									maxAmount={effectiveDepositableBalanceBigNum}
									isLoading={loadingWalletDepositableBalance}
								/>
							</div>
						</div>
						<div className="flex items-center justify-between">
							<span>Repay Borrowing</span>
							<div className="flex items-center gap-1">
								<InputQuickActions.FiftyPercentButton
									onClick={handleFiftyPercentRepay}
								/>
								<InputQuickActions.MaxButton
									onClick={handleMaxRepay}
									maxAmount={bufferedCloseBorrowAmount}
								/>
							</div>
						</div>
					</div>

					<Divider />

					<div className="flex flex-col gap-1">
						<div className="flex items-center justify-between gap-1">
							<span>New Asset Balance</span>
							<span className="text-text-default">
								{`${targetAccountBankBalance.netBaseBalance
									.add(collatTransferAmountBigNum)
									.prettyPrint()} ${getCleanedMarketSymbol(
									baseAssetDisplaySymbol
								)}`}
							</span>
						</div>
						<div className="flex items-center justify-between gap-1">
							<span>New Net Account Balance (USD)</span>
							<span className="text-text-default">
								{toNotional(projectedNetAccountValue.toNum())}
							</span>
						</div>
					</div>
				</div>
			</BaseModal.Body>
			<BaseModal.Footer>
				<Button
					className="w-full"
					size="lg"
					onClick={handleDeposit}
					disabled={isButtonDisabled}
					isLoading={isSubmitting}
					dataTestId="repay-modal-submit"
				>
					Confirm Deposit
				</Button>
			</BaseModal.Footer>
		</>
	);
};

const RepaySwapTab = ({
	spotMarketIndex,
	targetAccountKey,
	onClose,
}: {
	spotMarketIndex: number;
	targetAccountKey?: string;
	onClose: () => void;
}) => {
	const spotMarketsLookup = useSpotMarketsLookup();
	const spotMarketsSortedByMarketIndex = useSpotMarketsSortedByMarketIndex();
	const swapToMarket = spotMarketsSortedByMarketIndex[spotMarketIndex];
	const swapToPoolId = spotMarketsLookup[spotMarketIndex].poolId;
	const driftClient = useDriftClient();
	const isMobile = useIsMobileScreenSize();
	const [isSubmitting, setIsSubmitting] = useState(false);

	const initialSwapFrom =
		spotMarketsSortedByMarketIndex.find(
			(m) => m && m.poolId === swapToPoolId && m.marketIndex !== spotMarketIndex
		) ||
		spotMarketsSortedByMarketIndex.find(
			(m) => m?.symbol?.toUpperCase?.() === 'USDC'
		);

	const [swapFromMarket, setSwapFromMarket] =
		useState<SpotMarketConfig>(initialSwapFrom);

	const currentUserKey = useDriftAccountStore((s) => s.currentUserKey);
	const userKey = targetAccountKey ?? currentUserKey;

	const { fromTokenPrice, toTokenPrice } = useTokenPairData(
		swapFromMarket?.symbol,
		swapToMarket?.symbol
	);

	const {
		enableVersionedTx,
		enableVersionedTransaction,
		setSwapMode,
		swapFromAmount,
		setSwapFromAmount,
		swapToAmount,
		setSwapToAmount,
		swapPrice,
		handleSwap,
		isLoading,
		isButtonDisabled,
		maxSwapSize,
		isInsufficientBalance,
	} = useSwapFormEngine(
		swapFromMarket,
		swapToMarket,
		DEFAULT_SWAP_SLIPPAGE_TOLERANCE_PCT,
		'ExactOut',
		userKey
	);

	const handleSwapFromAmountUserChange = (val: string) => {
		setSwapMode('ExactIn');
		const truncated = truncateInputToPrecision(
			val,
			swapFromMarket.precisionExp
		);
		setSwapFromAmount(truncated);
	};

	const handleSwapToAmountUserChange = (val: string) => {
		setSwapMode('ExactOut');
		const truncated = truncateInputToPrecision(val, swapToMarket.precisionExp);
		setSwapToAmount(truncated);
	};

	const payMarketUI = UIMarket.createSpotMarket(swapFromMarket.marketIndex);
	const receiveMarketUI = UIMarket.createSpotMarket(swapToMarket.marketIndex);
	const payPrecision = (payMarketUI.market as SpotMarketConfig).precisionExp;
	const receivePrecision = (receiveMarketUI.market as SpotMarketConfig)
		.precisionExp;

	// Current borrowed balance (abs) on the asset we are receiving, buffered slightly to avoid interest underflow
	const targetAccountBankBalance = useAccountTargetSpotBalance(
		swapToMarket,
		userKey
	);
	const [savedSettings, setSavedSettings] = useCurrentSettings();

	// Disable swap leverage on repay by swap
	useEffect(() => {
		setSavedSettings({
			...savedSettings,
			enableSwapLeverage: false,
		});
	}, []);

	const useDirectRoute = savedSettings.directRouteSwaps;

	const bufferedCloseBorrowAmount = driftClient
		? getCloseBorrowAmountWithBuffer(
				driftClient,
				swapToMarket.marketIndex,
				targetAccountBankBalance?.netBaseBalance ?? BigNum.zero()
			)
		: BigNum.zero(receivePrecision);

	const handleMaxReceive = () => {
		handleSwapToAmountUserChange(bufferedCloseBorrowAmount.print());
	};

	const handleSubmit = async () => {
		setIsSubmitting(true);

		try {
			const result = await handleSwap();
			if (result === 'success') onClose();
		} catch (err) {
			console.error(err);
		}

		setIsSubmitting(false);
	};

	return (
		<>
			<BaseModal.Body className="pt-0">
				<div className="flex flex-col gap-4">
					<div className="flex flex-col gap-1">
						<CollateralInputV2
							maxAmount={maxSwapSize}
							selectedMarket={swapFromMarket}
							onChangeValue={(val) => handleSwapFromAmountUserChange(val ?? '')}
							onChangeMarket={setSwapFromMarket}
							value={swapFromAmount}
							label="Pay"
							source={{ type: 'userAccount', userKey }}
							poolId={swapToPoolId}
							excludedMarketIndices={[spotMarketIndex]}
							customElementRight={
								<InputQuickActions.SizePresets
									onClick={(amt) => handleSwapFromAmountUserChange(amt.print())}
									maxAmount={maxSwapSize}
								/>
							}
							fullScreenPopover={isMobile}
						/>
						<div className="text-right">
							<span className="typo-b4 text-text-tertiary">
								~
								{toNotional(
									(Number(swapFromAmount || '0') *
										(fromTokenPrice ?? 0)) as number
								)}
							</span>
						</div>
					</div>

					<div className="flex flex-col gap-1">
						<CollateralInputV2
							maxAmount={bufferedCloseBorrowAmount}
							selectedMarket={swapToMarket}
							onChangeValue={(val) => handleSwapToAmountUserChange(val ?? '')}
							value={swapToAmount}
							label="Receive"
							source={{ type: 'default' }}
							poolId={swapToPoolId}
							customElementRight={
								<InputQuickActions.CustomButton
									label="To Close:"
									onClick={handleMaxReceive}
									amount={bufferedCloseBorrowAmount}
								/>
							}
							fullScreenPopover={isMobile}
						/>
						<div className="text-right">
							<span>
								~
								{toNotional(
									(Number(swapToAmount || '0') * (toTokenPrice ?? 0)) as number
								)}
							</span>
						</div>
					</div>
					<div className="flex flex-col gap-xs">
						<div className="flex w-full items-center justify-between gap-1">
							<span>Rate</span>
							<SwapPrice
								inAmount={BigNum.fromPrint(swapFromAmount || '0', payPrecision)}
								outAmount={BigNum.fromPrint(
									swapToAmount || '0',
									receivePrecision
								)}
								inMarket={payMarketUI}
								outMarket={receiveMarketUI}
								swapPrice={swapPrice}
							/>
						</div>

						{!enableVersionedTx && (
							<InlineAlert
								variant="warning"
								title="You need to enable Versioned Transactions to use Swaps."
							>
								<Button size="sm" onClick={enableVersionedTransaction}>
									Enable
								</Button>
							</InlineAlert>
						)}
						{isInsufficientBalance && (
							<InlineAlert variant="warning" title="Insufficient Balance" />
						)}
						<CollapsibleSlippageToleranceInput
							slippageTolerance={DEFAULT_SWAP_SLIPPAGE_TOLERANCE_PCT}
						/>

						<div className="flex items-center justify-between">
							<DefinitionTooltip definitionId="tradingDirectRouteSwap">
								Use direct route
							</DefinitionTooltip>
							<Switch
								size="sm"
								checked={useDirectRoute}
								onCheckedChange={() =>
									setSavedSettings({
										...savedSettings,
										directRouteSwaps: !useDirectRoute,
									})
								}
							/>
						</div>
					</div>
				</div>
			</BaseModal.Body>
			<BaseModal.Footer>
				<Button
					className="w-full"
					size="lg"
					onClick={handleSubmit}
					disabled={isButtonDisabled}
					isLoading={isSubmitting}
					dataTestId="repay-modal-swap-submit"
				>
					{isLoading ? 'Loading' : 'Swap'}
				</Button>
			</BaseModal.Footer>
		</>
	);
};

const RepayModal = ({
	collateralIndex,
	collateralPoolId,
	targetAccountKey,
	defaultLargestBalance,
}: CollateralModalProps) => {
	const { hide } = useModal();

	const { selectedTargetAccountKey, selectedCollateralMarket } =
		useCollateralModalState({
			collateralIndex,
			collateralPoolId,
			targetAccountKey,
			defaultLargestBalance,
		});

	const [selectedTab, setSelectedTab] = useState<RepayTab>('deposit');

	const spotMarketIndex = selectedCollateralMarket.marketIndex;

	return (
		<BaseModal.Container onClose={hide} dataTestId="repay-modal">
			<BaseModal.Title onClose={hide} closeButtonTestId="repay-modal-close">
				Repay
			</BaseModal.Title>
			<div
				className={cj(
					MODAL_PADDING.horizontal,
					MODAL_PADDING.headerFooterVertical,
					MODAL_BG_CLASSNAME
				)}
			>
				<ToggleTabs
					options={RepayTabOptions}
					activeTab={selectedTab}
					onTabChange={setSelectedTab}
					padding={4}
					animatedBackgroundClassName="bg-button-primary-bg"
				/>
			</div>

			{selectedTab === 'deposit' && (
				<RepayDepositTab
					spotMarketIndex={spotMarketIndex}
					targetAccountKey={selectedTargetAccountKey}
					onClose={hide}
				/>
			)}

			{selectedTab === 'swaps' && (
				<RepaySwapTab
					spotMarketIndex={spotMarketIndex}
					targetAccountKey={selectedTargetAccountKey}
					onClose={hide}
				/>
			)}
		</BaseModal.Container>
	);
};

export default React.memo(RepayModal);
